import { MapFeature, MapFeatureType, RecordLink } from '../types.js';

export const POINT_KINDS = [
  { value: 'blind', label: 'Hunting blind', color: '#de1717', path: 'M3 10 12 3l9 7M5 9v11h14V9M8 12h8v4H8zM12 16v4' },
  { value: 'tree_stand', label: 'Tree stand', color: '#46764b', path: 'M8 3v18M4 7l4-4 4 4M4 11l4-4 4 4M8 13h11v3H8M16 16v5M12 16v5M12 19h4' },
  { value: 'barn', label: 'Barn', color: '#a3483a', path: 'm3 10 3-5 6-3 6 3 3 5M4 10v11h16V10M8 21V11h8v10M8 11l8 10M16 11 8 21' },
  { value: 'outbuilding', label: 'Outbuilding', color: '#65717c', path: 'M3 10 12 4l9 6M5 9v12h14V9M9 21v-8h6v8' },
  { value: 'cabin', label: 'Cabin', color: '#966744', path: 'm2 11 10-8 10 8M5 10v11h14V10M10 21v-8h4v8M16 6V3h3v6' },
  { value: 'gate', label: 'Gate', color: '#66754a', path: 'M3 5v16M21 5v16M3 8h18M3 17h18M3 8l18 9M3 17l18-9' },
  { value: 'camera', label: 'Camera', color: '#44697b', path: 'M3 7h5l2-3h4l2 3h5v13H3zM16 13a4 4 0 1 1-8 0 4 4 0 0 1 8 0' },
  { value: 'parking', label: 'Parking', color: '#44697b', path: 'M6 21V3h7a6 6 0 0 1 0 12H6' },
  { value: 'other', label: 'Other point', color: '#76668e', path: 'M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0' },
] as const;
export const isPhotoPlace = (place: Partial<MapFeature>) =>
  !place.deleted_at && String(place.geometry_type).toLowerCase() === 'point' && place.feature_type !== MapFeatureType.Trail;
export const pointKind = (place: Partial<MapFeature>): NonNullable<MapFeature['point_kind']> => {
  if (place.point_kind && POINT_KINDS.some(kind => kind.value === place.point_kind)) return place.point_kind;
  const name = place.label?.toLowerCase() || '';
  if (/\bblind\b/.test(name)) return 'blind';
  if (/\b(tree\s*stand|treestand)\b/.test(name)) return 'tree_stand';
  if (/\bbarn\b/.test(name)) return 'barn';
  return place.feature_type === MapFeatureType.Structure ? 'outbuilding' : 'other';
};
export const pointStyle = (place: Partial<MapFeature>) => POINT_KINDS.find(kind => kind.value === pointKind(place))!;
// The entire primary key is intentional: no collisions, no extra identifier to sync,
// and renaming, moving, or archiving a place never changes its import identity.
export const placePhotoTag = (place: Pick<MapFeature, 'map_feature_id'>) => `place:${place.map_feature_id}`;
export const placePhotoIds = (links: RecordLink[], placeId: string) => [...new Set(links.filter(link =>
  !link.deleted_at && ((link.entity_type === 'map_feature' && link.entity_id === placeId) ||
    (link.to_record_type === 'map_feature' && link.to_record_id === placeId) ||
    (link.from_record_type === 'map_feature' && link.from_record_id === placeId))
).map(link => link.photo_id || (link.from_record_type === 'scouting_photo' ? link.from_record_id : link.to_record_type === 'scouting_photo' ? link.to_record_id : '')).filter(Boolean))];
export const pointCoordinates = (place: Partial<MapFeature>): [number, number] | null => {
  try {
    const coords = mapCoordinates(place);
    return Array.isArray(coords) && coords.length === 2 && coords.every(Number.isFinite) ? coords as [number, number] : null;
  } catch { return null; }
};
/** Legacy arrays are Leaflet lat/lng; standard GeoJSON objects are lng/lat. */
export const mapCoordinates = (place: Partial<MapFeature>): any => {
  const parsed = JSON.parse(place.coordinates_geojson || 'null');
  if (Array.isArray(parsed)) return parsed;
  const coordinates = parsed?.coordinates || parsed?.geometry?.coordinates;
  const toLatLng = (coords: any): any => Array.isArray(coords) && typeof coords[0]==='number'
    ? [coords[1],coords[0]] : Array.isArray(coords) ? coords.map(toLatLng) : null;
  return toLatLng(coordinates);
};
// GPS assists selection only; overlapping pins need an explicit choice.
export const nearbyPhotoPlace = (places: MapFeature[], lat: number, lng: number) => {
  const radians = (value: number) => value * Math.PI / 180;
  const matches = places.filter(isPhotoPlace).map(place => {
    const point = pointCoordinates(place);
    if (!point) return {place,distance:Infinity};
    const a = Math.sin(radians(point[0]-lat)/2)**2 + Math.cos(radians(lat))*Math.cos(radians(point[0]))*Math.sin(radians(point[1]-lng)/2)**2;
    return {place,distance:6371000*2*Math.atan2(Math.sqrt(a),Math.sqrt(Math.max(0,1-a)))};
  }).filter(item=>item.distance<=75).sort((a,b)=>a.distance-b.distance);
  return matches.length && !(matches[1] && matches[1].distance-matches[0].distance<15) ? matches[0].place : null;
};

export const INACTIVE_POI_COLOR = '#9e9ca0';
export const pointColor = (place:Partial<MapFeature>, palette:Record<string,string>={}) => {
  if(place.is_inactive)return INACTIVE_POI_COLOR;
  const color=palette[pointKind(place)];
  return /^#[0-9a-f]{6}$/i.test(color||'')?color:pointStyle(place).color;
};
